import moment from 'moment';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { SORT_MAP } from '@/components/FeedSort';
import type { LocationState, RouteParam, SearchOptions } from '@/types';
import { normalizeFilters } from '@/utils/filters';

import { useGetSearchId } from './useGetSearchId';
import { useQueryParams } from './useQueryParams';

type ReturnType = {
  /*
   * this is the searchquery object containing filters, sort, daterange, feedname, and q. this is to be sent in api calls
   */
  localSearchQueryParam: Record<string, unknown>;
  /*
   * q is the query, q from url. primary search query
   */
  q: string;
  /*
   * board_id to be sent in api requests.
   * for custom feeds it is going to be the respective board_ids and for other it is going to be `home`
   */
  board_id?: string;
  /**
   * channel id
   */
  channel_id?: string;
  /**
   * notebook_id
   */
  notebook_id?: string;
  /**
   * durationOption - date_range in string format
   */
  durationOption?: string;
  /**
   * query is not spell corrected
   * @value false
   */
  spell_correct?: boolean;
};

export const useConstructSearchQueryParams = (): ReturnType => {
  const { state } = useLocation<LocationState>();
  const [q] = useQueryParams();
  const { subCategory } = useParams<RouteParam>();
  const { board_id, channel_id, notebook_id } = useGetSearchId();
  const stableState = JSON.stringify(state || {});
  const query = q?.q as string;
  const spellCorrect = q?.spell_correct === 'false';
  const search_type = q?.search_type as SearchOptions;

  return React.useMemo(() => {
    const state = JSON.parse(stableState);
    let localSearchQueryParam = {};

    let filter = {};
    let date_range = null;
    let durationOption = ''; // date_range in string format
    let sort = {};

    if (state?.filtersFromUI !== undefined && state?.filtersFromUI !== null) {
      filter = { ...filter, ...state?.filtersFromUI };
    }

    if (state?.dateRange === null) {
      date_range = null;
    } else if (state?.dateRange !== undefined && state?.dateRange !== null) {
      date_range = {
        start_date: state.dateRange.startDate,
        end_date: state.dateRange.endDate,
      };
      durationOption = state.dateRange.durationOption;
    }

    const sortOptionsFromUI = state?.sortOptionsFromUI;
    if (
      sortOptionsFromUI === null ||
      sortOptionsFromUI === undefined ||
      sortOptionsFromUI === ''
    ) {
      sort = SORT_MAP['relevance'];
    } else if (
      sortOptionsFromUI !== undefined &&
      sortOptionsFromUI !== null &&
      sortOptionsFromUI in SORT_MAP &&
      typeof sortOptionsFromUI === 'string'
    ) {
      sort = SORT_MAP[sortOptionsFromUI];
    }

    if (subCategory === 'today') {
      date_range = {
        start_date: moment().format('DD-MM-YYYY'),
        end_date: moment().format('DD-MM-YYYY'),
      };
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    localSearchQueryParam = {
      ...(Object.keys(filter).length > 0 && {
        filter: normalizeFilters(filter),
      }),
      ...(date_range &&
        date_range !== null &&
        Object.keys(date_range).length > 0 && {
          date_range,
        }),
      timezone,
      ...(query ? { q: query } : {}),
      ...(spellCorrect ? { spell_correct: false } : {}),
      sort: [sort],
      ...(channel_id && { channel_id }),
      ...(board_id && { board_id }),
      ...(notebook_id && { notebook_id }),
      ...(search_type && { search_type: search_type }),
    };

    return {
      localSearchQueryParam,
      q: query || '',
      ...(channel_id && { channel_id }),
      ...(board_id && { board_id }),
      ...(notebook_id && { notebook_id }),
      ...(durationOption && { durationOption }),
      ...(spellCorrect ? { spell_correct: false } : {}),
    };
  }, [
    stableState,
    subCategory,
    query,
    spellCorrect,
    channel_id,
    board_id,
    notebook_id,
    search_type,
  ]);
};
